import { isValidPhoneNumber } from "react-phone-number-input";

export const MAX_EMPLOYMENT_DAYS = 365;
export const MAX_NAME_LENGTH = 50;
export const MAX_TEXT_FIELD_LENGTH = 255;

const NAME_REGEX = /^[\p{L}\p{M}]+(?:[- '][\p{L}\p{M}]+)*$/u;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PERSONAL_IDENTITY_NUMBER_REGEX = /^(\d{12}|\d{8}-\d{4})$/;

type StaffInputTranslationKey =
  | "Staff.AddStaffPanelAgreementTooLong"
  | "Staff.AddStaffPanelEmailInvalid"
  | "Staff.AddStaffPanelEmailTooLong"
  | "Staff.AddStaffPanelEmployeeNumberInvalid"
  | "Staff.AddStaffPanelEmploymentDaysInvalid"
  | "Staff.AddStaffPanelFirstNameInvalid"
  | "Staff.AddStaffPanelFirstNameTooLong"
  | "Staff.AddStaffPanelLastNameInvalid"
  | "Staff.AddStaffPanelLastNameTooLong"
  | "Staff.AddStaffPanelPersonalIdentityNumberInvalid"
  | "Staff.AddStaffPanelPersonalIdentityNumberTooLong"
  | "Staff.AddStaffPanelPhoneNumberInvalid"
  | "Staff.AddStaffPanelRoleTooLong"
  | "Staff.AddStaffPanelWorkPercentageInvalid"
  | "Staff.AddStaffPanelWorkplaceTooLong";

export type StaffInputValidationError =
  | {
      readonly translationKey: StaffInputTranslationKey;
      readonly type: "invalidFormat";
    }
  | {
      readonly translationKey: StaffInputTranslationKey;
      readonly type: "outOfRange";
    }
  | {
      readonly translationKey: StaffInputTranslationKey;
      readonly type: "tooLong";
    };

type StaffNameField = "firstName" | "lastName";

interface EmployeeNumberValidationParams {
  readonly isRequired: boolean;
  readonly value: string;
}

interface NameValidationErrorParams {
  readonly field: StaffNameField;
  readonly value: string;
}

interface OptionalTextValidationErrorParams {
  readonly translationKey:
    | "Staff.AddStaffPanelAgreementTooLong"
    | "Staff.AddStaffPanelRoleTooLong"
    | "Staff.AddStaffPanelWorkplaceTooLong";
  readonly value: string;
}

// These rules mirror backend limits for user-entered values. Keeping their
// validation and error classification here prevents create/update drift.
// Empty mandatory fields intentionally have no inline error; save guards still
// block submission until those fields contain valid values.

function toInvalidFormatError(
  translationKey: StaffInputTranslationKey,
): StaffInputValidationError {
  return { translationKey, type: "invalidFormat" };
}

function toOutOfRangeError(
  translationKey: StaffInputTranslationKey,
): StaffInputValidationError {
  return { translationKey, type: "outOfRange" };
}

function toTooLongError(
  translationKey: StaffInputTranslationKey,
): StaffInputValidationError {
  return { translationKey, type: "tooLong" };
}

/** Whether a field has been answered at all, before any rule about what it holds. */
export function hasValue(value: string | undefined): boolean {
  return (value ?? "").trim().length > 0;
}

export function hasValidName({ value }: { value: string }): boolean {
  const normalizedValue = value.trim();
  return (
    normalizedValue.length > 0 &&
    normalizedValue.length <= MAX_NAME_LENGTH &&
    NAME_REGEX.test(normalizedValue)
  );
}

export function hasValidOptionalEmail({ value }: { value: string }): boolean {
  const normalizedValue = value.trim();
  return (
    normalizedValue.length === 0 ||
    (normalizedValue.length <= MAX_TEXT_FIELD_LENGTH &&
      EMAIL_REGEX.test(normalizedValue))
  );
}

export function hasValidOptionalText({ value }: { value: string }): boolean {
  return value.trim().length <= MAX_TEXT_FIELD_LENGTH;
}

export function hasValidOptionalPersonalIdentityNumber({
  value,
}: {
  value: string;
}): boolean {
  const normalizedValue = value.trim();
  return (
    normalizedValue.length === 0 ||
    (normalizedValue.length <= MAX_TEXT_FIELD_LENGTH &&
      PERSONAL_IDENTITY_NUMBER_REGEX.test(normalizedValue))
  );
}

export function hasValidRequiredText({ value }: { value: string }): boolean {
  const normalizedValue = value.trim();
  return (
    normalizedValue.length > 0 &&
    normalizedValue.length <= MAX_TEXT_FIELD_LENGTH
  );
}

export function hasValidOptionalEmploymentDays({
  value,
}: {
  value: string;
}): boolean {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) return true;
  if (!/^\d+$/.test(normalizedValue)) return false;
  const parsedValue = Number(normalizedValue);
  return (
    Number.isSafeInteger(parsedValue) &&
    parsedValue >= 0 &&
    parsedValue <= MAX_EMPLOYMENT_DAYS
  );
}

export function hasValidEmployeeNumber({
  isRequired,
  value,
}: EmployeeNumberValidationParams): boolean {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) return !isRequired;
  if (!/^\d+$/.test(normalizedValue)) return false;
  const parsedValue = Number(normalizedValue);
  return Number.isSafeInteger(parsedValue) && parsedValue > 0;
}

/**
 * Whether the number is one the country its calling code names actually issues,
 * checked against that country's number ranges rather than only its lengths.
 *
 * A number this accepts is always one `toApiPhoneNumber` can convert, since a
 * number that cannot be parsed cannot be valid either. The field therefore says
 * so while the person is still looking at it, rather than the conversion
 * refusing it once they have pressed save.
 */
export function hasValidPhoneNumber({ value }: { value: string }): boolean {
  return isValidPhoneNumber(value);
}

export function hasValidWorkPercentage({ value }: { value: string }): boolean {
  const normalizedValue = value.trim();
  const parsedValue = Number(normalizedValue);
  return (
    normalizedValue.length > 0 &&
    Number.isFinite(parsedValue) &&
    parsedValue >= 1 &&
    parsedValue <= 100
  );
}

export function getNameValidationError({
  field,
  value,
}: NameValidationErrorParams): StaffInputValidationError | null {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0 || hasValidName({ value })) return null;
  if (normalizedValue.length > MAX_NAME_LENGTH) {
    return toTooLongError(
      field === "firstName"
        ? "Staff.AddStaffPanelFirstNameTooLong"
        : "Staff.AddStaffPanelLastNameTooLong",
    );
  }
  return toInvalidFormatError(
    field === "firstName"
      ? "Staff.AddStaffPanelFirstNameInvalid"
      : "Staff.AddStaffPanelLastNameInvalid",
  );
}

export function getOptionalEmailValidationError({
  value,
}: {
  value: string;
}): StaffInputValidationError | null {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0 || hasValidOptionalEmail({ value })) {
    return null;
  }
  return normalizedValue.length > MAX_TEXT_FIELD_LENGTH
    ? toTooLongError("Staff.AddStaffPanelEmailTooLong")
    : toInvalidFormatError("Staff.AddStaffPanelEmailInvalid");
}

export function getOptionalTextValidationError({
  translationKey,
  value,
}: OptionalTextValidationErrorParams): StaffInputValidationError | null {
  if (hasValidOptionalText({ value })) return null;
  return toTooLongError(translationKey);
}

export function getOptionalPersonalIdentityNumberValidationError({
  value,
}: {
  value: string;
}): StaffInputValidationError | null {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) return null;
  if (normalizedValue.length > MAX_TEXT_FIELD_LENGTH) {
    return toTooLongError("Staff.AddStaffPanelPersonalIdentityNumberTooLong");
  }
  return hasValidOptionalPersonalIdentityNumber({ value })
    ? null
    : toInvalidFormatError("Staff.AddStaffPanelPersonalIdentityNumberInvalid");
}

export function getEmployeeNumberValidationError({
  value,
}: {
  value: string;
}): StaffInputValidationError | null {
  if (
    value.trim().length === 0 ||
    hasValidEmployeeNumber({ isRequired: false, value })
  ) {
    return null;
  }
  return toInvalidFormatError("Staff.AddStaffPanelEmployeeNumberInvalid");
}

export function getEmploymentDaysValidationError({
  value,
}: {
  value: string;
}): StaffInputValidationError | null {
  if (hasValidOptionalEmploymentDays({ value })) return null;
  return toOutOfRangeError("Staff.AddStaffPanelEmploymentDaysInvalid");
}

export function getPhoneValidationError({
  value,
}: {
  value: string;
}): StaffInputValidationError | null {
  if (value.trim().length === 0 || hasValidPhoneNumber({ value })) return null;
  return toInvalidFormatError("Staff.AddStaffPanelPhoneNumberInvalid");
}

export function getWorkPercentageValidationError({
  value,
}: {
  value: string;
}): StaffInputValidationError | null {
  if (value.trim().length === 0 || hasValidWorkPercentage({ value })) {
    return null;
  }
  return toOutOfRangeError("Staff.AddStaffPanelWorkPercentageInvalid");
}
